"use client";

import React, { useEffect, useState } from "react";
import Link from "next/link";
import { PortableText } from "@portabletext/react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { GROQCTA } from "@/sanity/lib/definitions";
import { useCTAWrapper } from "@/hooks/useCTAWrapper";
import Anchor from "@/components/global/utils/Anchor";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/shadcn/ui/dialog";
import { customRichTextComponent } from "@/sanity/sanityPortableText";
import HubSpotForm from "@/components/global/partials/HubSpotForm";
import ZapierForm from "@/components/global/partials/ZapierForm";
import Chip from "@/components/global/ui/Chip";
import { cn } from "@/lib/utils";

interface CTAWrapperProps {
  cta: GROQCTA | null | undefined;
  children: React.ReactNode;
  className?: string;
}

export function CTAWrapper({ cta, children, className }: CTAWrapperProps) {
  const { isLink, link, linkData, isAnchor, anchor, isPopup, popup } = useCTAWrapper(cta);

  const [finalLink, setFinalLink] = useState("");

  // Listen for link updates and update the final link accordingly
  useEffect(() => {
    if (!link) return;
    setFinalLink(link);
  }, [link]);

  if (isLink && finalLink) {
    const openInNewTab = linkData?.openInNewTab ?? false;
    return (
      <Link
        href={finalLink}
        className={cn("inline-block", className)}
        target={openInNewTab ? "_blank" : "_self"}
        rel={openInNewTab ? "noopener noreferrer" : undefined}>
        {children}
      </Link>
    );
  }

  if (isAnchor && anchor) {
    return (
      <Anchor
        target={anchor}
        className={cn("inline-block", className)}>
        {children}
      </Anchor>
    );
  }

  if (isPopup && popup) {
    const isNewTheme = popup.theme === "new";

    return (
      <Dialog>
        <DialogTrigger
          asChild
          className="">
          <div className={cn("w-full md:w-auto", className)}>{children}</div>
        </DialogTrigger>
        <DialogContent className="top-1/2 max-h-[80svh] -translate-y-1/2 overflow-y-auto rounded-[1.25rem] border border-black/[.15] bg-white/20 p-[.75rem] backdrop-blur-[2rem] lg:max-h-screen">
          <VisuallyHidden>
            <DialogHeader>
              <DialogTitle></DialogTitle>
              <DialogDescription></DialogDescription>
            </DialogHeader>
          </VisuallyHidden>

          <div className="relative flex w-full flex-col items-center overflow-clip rounded-[1rem] border bg-white px-[2.5rem] py-[3rem] sm:px-[3rem] md:px-[4rem] md:py-[5rem] lg:px-[5rem]">
            {popup?.chip && (
              <div className="prose-p:m-0 prose-p:font-spacemono prose-p:text-[2rem] prose-p:text-black prose-p:uppercase">
                <p className="">{popup.chip}</p>
              </div>
            )}

            {popup?.content && (
              <div className="richtext mb-[3rem] w-[min(50rem,100%)] prose-headings:text-[4rem] lg:prose-headings:text-[5.5rem]">
                <PortableText
                  value={popup.content}
                  components={customRichTextComponent({})}
                />
              </div>
            )}

            {popup?.formType === "hs" && popup?.hsForm && <HubSpotForm formData={popup?.hsForm} />}

            {popup?.formType === "zapier" && popup?.zapierForm && <ZapierForm formData={popup.zapierForm} />}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return <div className={cn("inline-block", className)}>{children}</div>;
}
